import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamListItem } from "@bezoned/shared/types/api/team/team-list.types.js";
import { Tooltip } from "@fluentui/react-components";
import {
  LockClosedRegular,
  ReOrderRegular,
  SettingsRegular,
} from "@fluentui/react-icons";
import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import type { DragSourceMonitor, DropTargetMonitor } from "react-dnd";

import { cn } from "../../../utils/tailwind-class-merge.js";
import { STARTER_DEFAULT_OFFICE_ONLY_MESSAGE } from "../../subscription/starter-plan-copy.js";

const DRAG_TYPE = "TEAM_ITEM";
const HOVER_THRESHOLD = 0.5;
const DRAGGING_OPACITY = 0.5;
const DEFAULT_OPACITY = 1;

interface TeamEditListItemProps {
  team: TeamListItem;
  index: number;
  moveTeam: (fromIndex: number, toIndex: number) => void;
  onDrop: (teamId: string, newIndex: number) => void;
  onEdit: () => void;
  highlighted?: boolean;
}

export const TeamEditListItem = ({
  team,
  index,
  moveTeam,
  onDrop,
  onEdit,
  highlighted = false,
}: TeamEditListItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const isRestricted = team.accessRestrictedReason !== null;
  const canEdit = team.role === "owner" && !isRestricted;

  const [{ isDragging }, drag, preview] = useDrag({
    canDrag: !isRestricted,
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (
      item: { index: number; team: typeof team },
      monitor: DragSourceMonitor
    ) => {
      if (!isRestricted && monitor.didDrop()) {
        const dropResult = monitor.getDropResult<{ index: number }>();
        if (dropResult) {
          // Use item.team.id (the dragged team) not team.id (current component's team)
          onDrop(item.team.id, item.index);
        }
      }
    },
    item: { index, team },
    type: DRAG_TYPE,
  });

  const [, drop] = useDrop({
    accept: DRAG_TYPE,
    drop: () => ({ index }),
    hover: (
      item: { index: number; team: typeof team },
      monitor: DropTargetMonitor
    ) => {
      if (!(ref.current && !isRestricted)) {
        return;
      }

      const fromIndex = item.index;
      const toIndex = index;

      if (fromIndex === toIndex) {
        return;
      }

      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) * HOVER_THRESHOLD;
      const clientOffset = monitor.getClientOffset();

      if (!clientOffset) {
        return;
      }

      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the item's height
      if (fromIndex < toIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (fromIndex > toIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveTeam(fromIndex, toIndex);
      item.index = toIndex;
    },
  });

  // Combine drag and drop refs on the main container
  if (isRestricted) {
    preview(ref);
  } else {
    preview(drop(drag(ref)));
  }
  const handleMouseDown144 = useStableCallback((e) => e.stopPropagation());

  const handleClick140 = useStableCallback((e) => {
    e.stopPropagation();
    if (!isRestricted) {
      onEdit();
    }
  });
  const editTooltip = (() => {
    if (canEdit) {
      return "Modify this office";
    }

    if (isRestricted) {
      return STARTER_DEFAULT_OFFICE_ONLY_MESSAGE;
    }

    return "Only the Admin of this office can perform changes";
  })();

  const content = (
    <div
      className={cn(
        "relative mb-4 flex w-full items-center gap-2 rounded-md p-4 text-white transition-colors ease-in-out",
        isRestricted
          ? "bg-bzd-gray-100 text-bzd-gray-500 border-bzd-gray-500 cursor-not-allowed border border-dashed"
          : "bg-bzd-purple-300 cursor-move"
      )}
      data-testid={`choose-team-${index}`}
      ref={ref}
      style={{ opacity: isDragging ? DRAGGING_OPACITY : DEFAULT_OPACITY }}
    >
      {highlighted ? (
        <Tooltip content="New" relationship="label">
          <span className="group absolute top-0 right-0 z-10 flex size-8 translate-x-1/2 -translate-y-1/2 cursor-help items-center justify-center">
            <span className="bg-bzd-red-400 size-4 rounded-full transition-transform duration-150 ease-out group-hover:scale-125" />
          </span>
        </Tooltip>
      ) : null}
      <ReOrderRegular
        className={cn(
          "size-6",
          isRestricted ? "text-bzd-gray-500" : "text-white"
        )}
        style={{ pointerEvents: "none" }}
      />

      <div className="flex-1 text-left text-lg font-medium">{team.name}</div>

      <Tooltip content={editTooltip} relationship="label">
        <button
          aria-label="Modify this office"
          className={cn(
            "inline-flex cursor-pointer items-center justify-center",
            !canEdit && "cursor-not-allowed opacity-50"
          )}
          disabled={!canEdit}
          onClick={handleClick140}
          onMouseDown={handleMouseDown144}
          type="button"
        >
          {isRestricted ? (
            <LockClosedRegular className="text-bzd-gray-500 size-6" />
          ) : (
            <SettingsRegular className="size-6 text-white" />
          )}
        </button>
      </Tooltip>
    </div>
  );

  if (!isRestricted) {
    return content;
  }

  return (
    <Tooltip content={STARTER_DEFAULT_OFFICE_ONLY_MESSAGE} relationship="label">
      <span className="block">{content}</span>
    </Tooltip>
  );
};
